import React from 'react';
import { PUZZLE_COMPONENT_TOOLTIP_PROPS, PUZZLE_FIELD_PROPS } from '../Types';
import { MaskedTextBoxProps } from '@progress/kendo-react-inputs';
import { PUZZLE_BUTTON_PROPS } from '../../button/Types';
import { FORM_CONTROL } from '../../../hoc/form/Types';
import { InputProps } from '@progress/kendo-react-inputs/dist/npm/input/Input';
import { PUZZLE_COPY_BUTTON_PROPS } from '../../button/copy/Types';
import { ButtonProps } from '@progress/kendo-react-buttons';

export interface INPUT_ON_KEY_DOWN { event: KeyboardEvent, target: HTMLElement }

export interface PUZZLE_INPUT_ACTION_CONFIG extends ButtonProps {
  /**
   * Action button aria label
   */
  ariaLabel?: string | undefined
  /**
   * Action button type
   */
  buttonType?: 'delete' | string
  /**
   * Tag for cypress
   */
  'data-cy'?: string
  /**
   * Show tooltip
   */
  withTooltip?: boolean
  titleText?:any
  iconClass?: string
}

interface INPUT_PROPS extends PUZZLE_COMPONENT_TOOLTIP_PROPS, PUZZLE_FIELD_PROPS {
  /**
   * Settings for action button inside input
   */
  actionBtnSettings?: PUZZLE_INPUT_ACTION_CONFIG[]
  /**
   * CSS class which will be added to the input itself
   */
  inputClassName?: string
  /**
   * Adds search icon on the left of the field
   */
  searchField?: boolean
  /** Changes the input type to password */
  password?: boolean
  /**
   * Locked input
   */
  locked?: boolean
  /**
   * Sets field valid state
   */
  setValid?: (resourceExists: boolean) => void
}

export interface INPUT_WITH_ACTIONS_PROPS extends InputProps, INPUT_PROPS {}
export type PUZZLE_INPUT_WITH_ACTIONS_PROPS_FIELD_SIZE_PROP = 'small' | 'medium' | 'large';
export interface PUZZLE_INPUT_KEY_EVENT { event: KeyboardEvent, target: { value: string } }
export interface PUZZLE_INPUT_PROPS extends INPUT_PROPS, MaskedTextBoxProps {
  onKeyDown?: (eventObject: PUZZLE_INPUT_KEY_EVENT) => void
  onPaste?: (eventObject: { event: React.ClipboardEvent<HTMLInputElement>, target: { value: string }}) => void
  onDrop?: (eventObject: { event: React.DragEvent<HTMLInputElement>, target: { value: string }}) => void
  onKeyUp?: (eventObject: PUZZLE_INPUT_KEY_EVENT) => void
}

export type PUZZLE_INPUT_WITH_ACTION_ITEM = {
  /**
   * Action button aria label
   */
  ariaLabel?: string | undefined
  iconClass?: string
  /**
   * Tag for cypress
   */
  'data-cy'?: string
  /**
   * Configures the `fillMode` of the MaskedTextBox.
   *
   * The available options are:
   * - solid
   * - outline
   * - flat
   * - null&mdash;Does not set a fillMode `className`.
   *
   * @default `solid`
   */
  fillMode?: null | 'solid' | 'flat' | 'outline';
  /**
   * Show tooltip
   */
  withTooltip?: boolean
  titleText?: string
  disabled?: boolean
  onClick: Function
  size?: PUZZLE_INPUT_WITH_ACTIONS_PROPS_FIELD_SIZE_PROP;
  /**
   * Icon
   */
  icon?: string
} | JSX.Element

export interface PUZZLE_INPUT_WITH_ACTIONS_PROPS extends PUZZLE_COMPONENT_TOOLTIP_PROPS, PUZZLE_FIELD_PROPS {
  /**
   * Input copmponent configuration
   */
  inputProps?: INPUT_WITH_ACTIONS_PROPS
  actionsBefore?: PUZZLE_INPUT_WITH_ACTION_ITEM[]
  actionsAfter?: PUZZLE_INPUT_WITH_ACTION_ITEM[]
  ariaDescribedBy?: string
  fieldSize?: PUZZLE_INPUT_WITH_ACTIONS_PROPS_FIELD_SIZE_PROP,
  className?: string
  disabled?: boolean
  value?: string
  valid?: boolean
  locked?: boolean
  ref?: any
  id?: string
  /**
   * Sets field valid state
   */
  setValid?: (resourceExists: boolean) => void
}

export interface PUZZLE_INPUT_WITH_ACTIONS_STATE {
  isInputFocused:boolean
}

export enum EVENTS_ACTION {
  add = 'addEventListener',
  remove = 'removeEventListener'
}

export interface OPTIONS {
  /**
   * Name of field
   */
  name: string;
  /**
   * Value of field
   */
  value: string;
}

export interface REQUEST_CONFIG_TYPE {
  /**
   * url: that corresponds to the endpoint for checking if the resource exists
   */
  url: string;
  /**
   * boolean: true if we want to use urlParams and false if we want to use params for requests
   */
  isSearchParams: boolean;
  /**
   * Additional params (default params property is `name`)
   */
  params?: any
  /**
   * paramsName changes the Request URL
   * In Request URL: /testUsers?email=user@found.com
   * email is paramsName
   * default value is prop name
   */
  paramsName?: string
}

export interface INPUT_WITH_EXISTING_VALIDATION_CHANGE_EVENT { name:string, value:string }

export interface PUZZLE_INPUT_WITH_EXISTING_VALIDATION_PROPS extends Omit<PUZZLE_INPUT_PROPS, 'onChange'> {
  requestConfig: REQUEST_CONFIG_TYPE;
  /**
   * getFieldValidation: method from AbstractBaseForm used to update the 'resourceExists'
   */
  getFieldValidation: (isFieldValid: boolean, name: string, value:string) => void;
  /**
   * Modify value of head request
   * @param {string} value
   */
  modifyHeadValue?: (value: string) => string;
  /**
   * The listener is triggered when the value of the field changes
   * @param {string} value
   * @returns {boolean} head request should be sent or not
   */
  shouldValidateHead?: (value: string) => boolean
  /**
   * Form from withFormControl HOC
   */
  form?: FORM_CONTROL
  /**
   * Determines the event handler that will be fired when the user edits the value.
   */
  onChange: (e: INPUT_WITH_EXISTING_VALIDATION_CHANGE_EVENT) => void;
}

export interface PUZZLE_PASSWORD_INPUT_PROPS extends PUZZLE_PASSWORD_INPUT_INPUT_PROPS, PUZZLE_COMPONENT_TOOLTIP_PROPS, PUZZLE_FIELD_PROPS {
  /**
   * Specifies whether to show the password visibility icon.
   */
  withShowPasswordIcon?: boolean;

  /**
   * Function to toggle the password visibility.
   */
  togglePasswordVisibility?: () => void;

  /**
   * The input type for the password input field.
   */
  inputType?: string;

  /**
   * Indicates whether to show the copy button.
   */
  showCopy?: boolean;
  /**
   * Value of input
   */
  value?: string
  /**
   * Sets field valid state
   */
  setValid?: (resourceExists: boolean) => void
  /**
   * Props for the copy button.
   */
  copyButtonProps?: PUZZLE_BUTTON_PROPS;
  /**
   * Position of the copy button. Possible values: "inside", "outside"
   */
  copyButtonPosition?: 'outside' | 'inside'
  fieldSize?: string
}

export interface PUZZLE_PASSWORD_INPUT_INPUT_PROPS extends INPUT_PROPS, PUZZLE_COMPONENT_TOOLTIP_PROPS, InputProps {}

export interface PUZZLE_INPUT_WITH_COPY {
  /**
   * Props for the input field.
   */
  inputProps: PUZZLE_PASSWORD_INPUT_PROPS;

  /**
   * Props for the copy button.
   */
  copyButtonProps?: PUZZLE_COPY_BUTTON_PROPS;

  /**
   * The position of the copy button relative to the input field.
   * If not provided, the copy button will not be rendered.
   */
  copyButtonPosition: 'outside' | 'inside'
}

export interface PUZZLE_INPUT_WITH_COPY_STATE {
  showPassword?: boolean
}
